import { useCallback,  useState } from "react";
import { Button, Search, Switch } from "@appkit4/react-components";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import Paginate from "components/common/paginate";
import { LoaderWrapper } from "components/common/helpers";
import { Floater, RoleItem } from "routes/analysis/helpers/analysisHelpers";
import { ActiveFilters } from "components/layout/filters/filters";
import { useAtomValue } from "jotai";
import { filterAtom } from "jotai/store";
import { updateParams } from "services/common";
import { FetchRoles } from "../queries/role";
import RoleTestList from "./RoleTestList";

const RoleList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { analysisId, roleId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [filterComposite, setFilterComposite] = useState(searchParams.get("composite") === "1" ? true : false);
  const [filterUsers, setFilterUsers] = useState(searchParams.get("attached") === "1" ? true : false);

  const getAttached = useCallback(() => searchParams.get("attached") === "1" ? true : false, [searchParams]);
  const getComposite = useCallback(() => searchParams.get("composite") === "1" ? true : false, [searchParams]);
  const getCurrentPage = useCallback(() => parseInt(searchParams.get("p") || "1"), [searchParams]);
  const getOffset = useCallback(() => parseInt(searchParams.get("o") || "50"), [searchParams]);
  const getSearch = useCallback(() => searchParams.get("search") || "", [searchParams]);

  const isRoleMatch = useCallback((id: string) => {
    return id === roleId
  }, [roleId]);

  const applySearch = () => {
    setSearchParams(updateParams("search", search, searchParams));
    setSearchParams(updateParams("attached", filterUsers ? 1 : 0, searchParams));
    setSearchParams(updateParams("composite", filterComposite ? 1 : 0, searchParams));
  }

  const resetSearch = () => {
    setSearch("");
    setFilterUsers(false);
    setFilterComposite(false);
    setSearchParams(updateParams("search", "", searchParams));
    setSearchParams(updateParams("attached", 0, searchParams));
    setSearchParams(updateParams("composite", 0, searchParams));
  }
  const userFilter = useAtomValue(filterAtom);

  const activeFilters = search.length > 2 || filterComposite || filterUsers;

  const { isPending, data, isFetching, error } = FetchRoles(analysisId, userFilter, (getCurrentPage() * 50) - 50, getAttached(), getComposite(), getSearch());

  return (
    <div id="list-top">
      <div className="flex items-center gap-4 mt-4">
        <div>
          <Search
            searchType={"secondary"}
            onChange={(value: string) => {
              setSearch(value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") applySearch();
            }}
            searchValue={search}
            className="list-filter"
          />
        </div>
        <div>
          <div>
            <Switch onChange={(value: boolean) => {
              setFilterUsers(value);
            }} checked={filterUsers} showIndicator>Roles with users only</Switch>
          </div>
        </div>
        <div>
          <div>
            <Switch onChange={(value: boolean) => {
              setFilterComposite(value);
            }} checked={filterComposite} showIndicator>Composite roles</Switch>
          </div>
        </div>
        <div className="basis-28">
          {activeFilters && <Button onClick={() => resetSearch()} kind="secondary" style={{ display: "block" }}>Clear</Button>}
        </div>
        <div>
          <Button onClick={() => applySearch()}>Apply</Button>
        </div>
      </div>
      {(!isPending && data) && <ActiveFilters rows={data?.pagination.totalItems}></ActiveFilters>}
      <Floater>
        <div
          className="ap-list-header ap-business-process-header flex gap-4 p-4 pt-6 pb-6 items-center w-full"
        >
          <div className="basis-12"></div>
          <div className="basis-80">
            Role
          </div>
          <div className="basis-32">
            Role type
          </div>
          <div className="basis-28">
            One or more matches
          </div>
          <div className="basis-52">
            Users out of total
          </div>
          <div>
            Uses with the role
          </div>
        </div>
      </Floater>
      <LoaderWrapper loading={[isPending, isFetching]} errors={[error]} inline>
        <div className="list-roles">
          {
            data?.roles.map((role) => (
              <div className="list-row" key={role.roleId} id={role.roleId}>
                <RoleItem role={role} open={isRoleMatch(role.roleId)} onClick={() => {
                  if (isRoleMatch(role.roleId)) {
                    navigate(`/analysis/${analysisId}/roles/${location.search}`);
                  } else {
                    if (role.hasMatches || (role.hasMatches && role.isComposite)) {
                      navigate(`/analysis/${analysisId}/roles/${role.roleId}${location.search}`)
                    }
                  }
                }} />
                {
                  isRoleMatch(role.roleId) && <RoleTestList role={role} />
                }
              </div>
            ))}
        </div>
      </LoaderWrapper>
      {data && (<Paginate
        noPageSet
        getTotalPages={Math.ceil(data?.pagination.totalItems / getOffset())}
      />)}
    </div>
  );
}

export default RoleList;
